/**
 * Area-specific copy for 5xx failures, keyed on request path. This is what
 * makes a message specific ("the quote card image couldn't be generated")
 * rather than generic, and it is the file to extend when the Monitoring board
 * shows a failure worth its own sentence.
 *
 * Each entry says what failed AND what it means for the user's work — whether
 * anything was saved, sent or charged. That second half is the part people
 * actually need.
 */
export const AREA_5XX = [
  {
    match: /\/api\/quote-card/,
    code: "QUOTE_CARD_UNAVAILABLE",
    message:
      "The quote card image couldn't be generated. The rest of the quote is unaffected.",
  },
  {
    match: /\/api\/xero/,
    code: "XERO_UNAVAILABLE",
    message: "Xero isn't responding right now. Nothing was sent — try again shortly.",
  },
  {
    match: /\/api\/(stripe|payment|deposit)/,
    code: "PAYMENT_PROVIDER_DOWN",
    message: "The payment provider isn't responding. No charge was made.",
  },
  {
    match: /\/api\/(files|pdfs)/,
    code: "FILE_UNAVAILABLE",
    message: "That file couldn't be loaded. Try again in a moment.",
  },
  {
    match: /\/api\/(marketplace|chrono24|watchfinder|ebay)/,
    code: "MARKETPLACE_UNAVAILABLE",
    message: "The marketplace connection isn't responding. Nothing was published.",
  },
  {
    match: /\/api\/(whatsapp|trade-feed)/,
    code: "FEED_UNAVAILABLE",
    message: "The trade feed isn't responding right now.",
  },
  {
    match: /\/api\/(gemini|ai)\b/,
    code: "AI_UNAVAILABLE",
    message: "The AI assistant isn't available right now.",
  },
];

/**
 * Requests that must never raise a toast or a report. The error reporter's own
 * endpoint is here because a failure to report an error would otherwise report
 * an error, and so on.
 */
export const SILENT_PATHS = [/\/api\/log\/errors/];

/**
 * Plan-gating refusals. EntitlementErrorListener owns these — it shows the
 * server's own upgrade copy — so the global handler must stay quiet or the
 * user gets the same news twice.
 */
export const GATING_CODES = ["feature_locked", "feature_limit"];

/**
 * Outages the SERVER already records on the Monitoring board, once per outage
 * window, with the real cause attached. The client stays quiet on these: a
 * quote page renders a card per item (two per item, with and without the year
 * filter), and reporting each one is how a single dead renderer became four
 * Monitoring groups on 2026-10-01. The component that made the request decides
 * what to show — see order/components/Quote/PriceIQ.jsx.
 */
export const SERVER_REPORTED_CODES = ["QUOTE_CARD_UNAVAILABLE"];

/**
 * 409s that are an expected state the requesting component explains itself,
 * not a fault. GOOGLE_RECONNECT_REQUIRED (GET /api/google-calendar/events):
 * Google rejected the user's stored token or the refresh lapsed, so they only
 * need to reconnect — the Calendar card on the sales-rep page offers that.
 * Never toasted, never reported. Any other 409 from that endpoint (a Google
 * refusal we did not expect) still reports as usual.
 */
export const EXPECTED_STATE_CODES = ["GOOGLE_RECONNECT_REQUIRED"];
